<template>
  <div class="py-10 overflow-y-scroll w-full h-full scroll-styling">
    <div class="container mx-auto px-4">
      <router-link
        :to="{ name: 'ClientEducation' }"
        class="mb-6 flex items-center gap-2 text-blue font-semibold transition hover:text-blue-700">
        ← Вернуться к курсам
      </router-link>

      <div v-if="loading" class="text-center py-10">
        <p class="text-lg text-gray-600">Загрузка данных курса...</p>
      </div>
      <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-6 text-red-700 mb-6">
        <p>{{ error }}</p>
      </div>

      <div v-if="course && !loading" class="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div class="relative h-96 md:h-[500px] overflow-hidden bg-gradient-to-br from-gray-200 to-gray-300">
          <div
            :style="{
              backgroundImage: course.image
                ? `url(${course.image})`
                : 'url(/assets/images/download.jpeg)',
            }"
            class="w-full h-full bg-contain bg-center bg-no-repeat">
          </div>

          <div class="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent flex items-end p-8">
            <div class="text-white">
              <h1 class="text-4xl md:text-5xl font-bold mb-4">{{ course.title }}</h1>
            </div>
          </div>

          <div v-if="course.price > 0" class="absolute top-6 right-6 bg-emerald-500 text-white px-5 py-3 rounded-full text-xl font-bold shadow-lg">
            {{ course.price }} ₸
          </div>
        </div>

        <div class="p-8 md:p-12">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12 pb-8 border-b border-gray-200">
            <div v-if="course.teacher" class="flex items-start gap-4">
              <div class="text-3xl">👨‍🏫</div>
              <div>
                <p class="text-sm text-gray-500 uppercase tracking-wide font-semibold">Преподаватель</p>
                <p class="text-lg font-semibold text-gray-800">{{ course.teacher }}</p>
              </div>
            </div>

            <div class="flex items-start gap-4">
              <div class="text-3xl">📚</div>
              <div>
                <p class="text-sm text-gray-500 uppercase tracking-wide font-semibold">Формат обучения</p>
                <p class="text-lg font-semibold text-gray-800">{{ course.course_type.val }}</p>
              </div>
            </div>

            <div class="flex items-start gap-4">
              <div class="text-3xl">💺</div>
              <div>
                <p class="text-sm text-gray-500 uppercase tracking-wide font-semibold">Доступно мест</p>
                <p class="text-lg font-semibold text-gray-800">{{ course.max_amount }}</p>
              </div>
            </div>

            <div v-if="course.hours" class="flex items-start gap-4">
              <div class="text-3xl">⏱️</div>
              <div>
                <p class="text-sm text-gray-500 uppercase tracking-wide font-semibold">Продолжительность</p>
                <p class="text-lg font-semibold text-gray-800">{{ course.hours }} часов</p>
              </div>
            </div>
          </div>

          <div class="mb-12">
            <h2 class="text-2xl font-bold text-gray-800 mb-4">Описание</h2>
            <p class="text-gray-700 text-lg leading-relaxed whitespace-pre-wrap">{{ course.description }}</p>
          </div>

          <div v-if="course.content" class="mb-12">
            <h2 class="text-2xl font-bold text-gray-800 mb-6">Программа курса</h2>
            <div
              v-html="course.content"
              class="prose prose-lg max-w-none text-gray-700 leading-relaxed
                prose-headings:text-gray-800 prose-headings:font-bold
                prose-p:mb-4 prose-li:mb-2
                prose-strong:text-gray-900 prose-strong:font-bold
                prose-a:text-emerald-600 prose-a:hover:text-emerald-700
                prose-ul:pl-6 prose-ol:pl-6">
            </div>
          </div>

          <div class="flex flex-col sm:flex-row gap-4 pt-8 border-t border-gray-200">
            <!-- If not authenticated - redirect to registration -->
            <router-link
              v-if="!isAuthenticated"
              v-slot="{ href, navigate }"
              :to="{
                name: 'ClientRegister',
                query: {
                  courseUUID: course.uuid,
                },
              }"
              class="flex-1">
              <a
                :href="href"
                @click="navigate"
                class="block w-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold py-3 px-6 rounded-lg hover:from-emerald-600 hover:to-emerald-700 transition-all duration-200 shadow-md hover:shadow-lg text-center uppercase tracking-wide">
                Оставить заявку
              </a>
            </router-link>

            <!-- If authenticated student and has status - show status badge -->
            <div v-else-if="isStudent && course.status" class="flex-1 px-6 py-3 rounded-lg text-center font-bold uppercase tracking-wide border-2"
              :class="{
                'bg-green-50 border-green-500 text-green-700': course.status === 'Одобрил',
                'bg-yellow-50 border-yellow-500 text-yellow-700': course.status === 'Не рассмотрена',
                'bg-red-50 border-red-500 text-red-700': course.status === 'Отклонил',
                'bg-blue-50 border-blue-500 text-blue-700': course.status && !['Одобрил', 'Не рассмотрена', 'Отклонил'].includes(course.status),
              }">
              Статус: {{ course.status }}
            </div>

            <!-- If authenticated student and no status - open application form -->
            <button
              v-else-if="isStudent"
              @click="openApplicationModal"
              class="flex-1 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold py-3 px-6 rounded-lg hover:from-emerald-600 hover:to-emerald-700 transition-all duration-200 shadow-md hover:shadow-lg text-center uppercase tracking-wide">
              Оставить заявку
            </button>

            <router-link
              :to="{ name: 'ClientEducation' }"
              class="flex-1">
              <button
                class="w-full bg-gray-200 text-gray-800 font-bold py-3 px-6 rounded-lg hover:bg-gray-300 transition-all duration-200 text-center uppercase tracking-wide">
                Вернуться к курсам
              </button>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapState } from 'vuex';
import { API_CLIENT, HEADERS_TOKEN_JSON } from '@/const/uri';

export default {
  name: 'CourseDetail',
  data() {
    return {
      course: null,
      loading: true,
      error: null,
      isAuthenticated: false,
    };
  },
  computed: {
    ...mapState(['user']),
    isStudent() {
      return this.isAuthenticated && this.user?.role?.key === 1;
    },
  },
  mounted() {
    this.checkAuthentication();
    this.fetchCourseDetail();
  },
  methods: {
    checkAuthentication() {
      const token = localStorage.getItem('user_token');
      this.isAuthenticated = !!token;
    },
    async fetchCourseDetail() {
      try {
        this.loading = true;
        this.error = null;

        const courseUuid = this.$route.params.courseId;
        if (!courseUuid) {
          this.error = 'ID курса не найден';
          return;
        }

        const response = await axios.get(
          `${API_CLIENT.courses}${courseUuid}/`,
          { headers: HEADERS_TOKEN_JSON() }
        );

        if (response.data.code === 0 || response.status === 200) {
          this.course = response.data.data;
        } else {
          this.course = response.data.data;
        }
      } catch (error) {
        console.error('Ошибка загрузки курса:', error);
        this.error = 'Не удалось загрузить данные курса. Пожалуйста, попробуйте позже.';
      } finally {
        this.loading = false;
      }
    },
    openApplicationModal() {
      // Redirect to /client/courses with courseUUID and showApplication=true to open form directly
      this.$router.push({
        path: '/client/courses',
        query: { courseUUID: this.course.uuid, showApplication: 'true' }
      });
    },
  },
};
</script>

<style scoped>
.prose {
  font-size: 1rem;
}

.prose h1,
.prose h2,
.prose h3,
.prose h4,
.prose h5,
.prose h6 {
  margin-top: 1.5em;
  margin-bottom: 0.75em;
}

.prose p {
  margin-bottom: 1em;
}

.prose ul,
.prose ol {
  margin-bottom: 1em;
}

.prose li {
  margin-bottom: 0.5em;
}

.prose strong {
  font-weight: 700;
  color: #1f2937;
}

.prose a {
  color: #059669;
  text-decoration: none;
}

.prose a:hover {
  color: #047857;
  text-decoration: underline;
}

.prose table {
  border-collapse: collapse;
  width: 100%;
  margin: 1em 0;
}

.prose table td,
.prose table th {
  border: 1px solid #e5e7eb;
  padding: 0.75em;
}

.prose table th {
  background-color: #f3f4f6;
  font-weight: 600;
}
</style>
